import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../AuthContext";

const Page = ({ children }) => (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
        <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl p-6">{children}</div>
    </div>
);

const { rootUrl } = require('../../../config/config');

export default function PartTypes() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [editingType, setEditingType] = useState(null);
    const [form, setForm] = useState({
        type: "",
        short_desc: "",
        long_desc: "",
        sign: "+",
        sub_type: "",
        def: false,
        notify: false,
        input_by: "system",
    });
    const [errors, setErrors] = useState({});

    useEffect(() => { fetchAll(); }, []);

    const fetchAll = async () => {
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const res = await axios.get(`${rootUrl}/parts/pay-types`, { headers: { Authorization: `Bearer ${token}` } });
            setItems(res.data?.data || res.data || []);
        } catch (err) { console.error(err); }
    };

    const onChange = (field) => (e) => {
        const value = e?.target ? e.target.value : e;
        setForm((f) => ({ ...f, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => { const ne = { ...prev }; delete ne[field]; return ne; });
        }
    };

    const validate = () => {
        let newErrors = {};
        if (!form.type) newErrors.type = "Type is required";
        if (!form.short_desc) newErrors.short_desc = "Name is required";
        if (!form.input_by) newErrors.input_by = "Input By is required";
        if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return false; }
        return true;
    };

    const createItem = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const payload = {
                type: form.type.trim().toUpperCase(),
                short_desc: form.short_desc.trim(),
                long_desc: form.long_desc || null,
                sign: form.sign || "+",
                sub_type: form.sub_type || null,
                def: form.def,
                notify: form.notify,
                input_by: user?.username || form.input_by,
                edit_by: user?.username || 'system',
            };

            let res;
            if (editingType) {
                // Update existing
                res = await axios.put(`${rootUrl}/parts/pay-types/${editingType}`, payload, { headers: { Authorization: `Bearer ${token}` } });
            } else {
                // Create new
                res = await axios.post(`${rootUrl}/parts/pay-types`, payload, { headers: { Authorization: `Bearer ${token}` } });
            }

            if (res.data?.success) {
                setForm((f) => ({ ...f, type: "", short_desc: "", long_desc: "", sign: "+", sub_type: "", def: false, notify: false }));
                setEditingType(null);
                await fetchAll();
            }
        } catch (err) { console.error(err?.response?.data?.message || err.message); }
    };

    const toggleActive = async (type, isActive) => {
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const res = await axios.put(`${rootUrl}/parts/pay-types/${type}`, { is_active: !isActive, edit_by: user?.username || 'system' }, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data?.success) {
                setItems((prev) => prev.map((it) => it.type === type ? { ...it, is_active: !isActive } : it));
            }
        } catch (err) { console.error(err?.response?.data?.message || err.message); }
    };

    const editItem = (item) => {
        setEditingType(item.type);
        setForm({
            type: item.type,
            short_desc: item.short_desc,
            long_desc: item.long_desc || "",
            sign: item.sign || "+",
            sub_type: item.sub_type || "",
            def: item.def || false,
            notify: item.notify || false,
            input_by: item.input_by || "system",
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelEdit = () => {
        setEditingType(null);
        setForm({ type: "", short_desc: "", long_desc: "", sign: "+", sub_type: "", def: false, notify: false, input_by: "system" });
        setErrors({});
    };

    const deleteItem = async (type, counter) => {
        if (counter > 0) {
            alert(`Cannot delete ${type}: counter is ${counter}. Only types with zero counter can be deleted.`);
            return;
        }
        if (!window.confirm(`Are you sure you want to delete ${type}?`)) return;
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const res = await axios.delete(`${rootUrl}/parts/pay-types/${type}`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data?.success) {
                await fetchAll();
            }
        } catch (err) {
            alert(err?.response?.data?.message || err.message);
            console.error(err);
        }
    };

    return (
        <Page>
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-semibold text-[#2a2e30]">{editingType ? `Edit Part Type: ${editingType}` : 'Part Types'}</h1>
                {editingType && (
                    <button type="button" onClick={cancelEdit} className="px-4 py-2 bg-gray-400 text-white rounded-lg">Cancel Edit</button>
                )}
            </div>

            <form onSubmit={createItem} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                    <label className="block text-sm text-gray-700">Type Code *</label>
                    <input value={form.type} onChange={onChange("type")} disabled={!!editingType} className="w-full mt-1 border rounded-lg px-3 py-2 uppercase disabled:bg-gray-100" placeholder="e.g. PUR" />
                    {errors.type && <p className="text-red-600 text-xs mt-1">{errors.type}</p>}
                </div>
                <div>
                    <label className="block text-sm text-gray-700">Name *</label>
                    <input value={form.short_desc} onChange={onChange("short_desc")} className="w-full mt-1 border rounded-lg px-3 py-2" placeholder="Purchase" />
                    {errors.short_desc && <p className="text-red-600 text-xs mt-1">{errors.short_desc}</p>}
                </div>
                <div>
                    <label className="block text-sm text-gray-700">Sign</label>
                    <select value={form.sign} onChange={onChange("sign")} className="w-full mt-1 border rounded-lg px-3 py-2">
                        <option value="+">+</option>
                        <option value="-">-</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm text-gray-700">Sub Type</label>
                    <input value={form.sub_type} onChange={onChange("sub_type")} className="w-full mt-1 border rounded-lg px-3 py-2" placeholder="Optional sub type" />
                </div>
                <div className="md:col-span-2 flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={form.def} onChange={(e) => onChange("def")(e.target.checked)} />
                        Default
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={form.notify} onChange={(e) => onChange("notify")(e.target.checked)} />
                        Notify
                    </label>
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm text-gray-700">Description</label>
                    <textarea value={form.long_desc} onChange={onChange("long_desc")} rows={3} className="w-full mt-1 border rounded-lg px-3 py-2" />
                </div>
                <div className="md:col-span-2 flex items-center justify-end gap-3">
                    {editingType && (
                        <button type="button" onClick={cancelEdit} className="px-4 py-2 bg-gray-400 text-white rounded-lg">Cancel</button>
                    )}
                    <button type="submit" className="px-4 py-2 bg-[#FF5733] text-white rounded-lg">{editingType ? 'Update Type' : 'Save Type'}</button>
                </div>
            </form>

            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-left border-b">
                            <th className="py-2 pr-4 w-32">Code</th>
                            <th className="py-2 pr-4">Name</th>
                            <th className="py-2 pr-4">Sign</th>
                            <th className="py-2 pr-4">Sub Type</th>
                            <th className="py-2 pr-4">Default</th>
                            <th className="py-2 pr-4">Active</th>
                            <th className="py-2 pr-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it) => (
                            <tr key={it.type} className="border-b">
                                <td className="py-2 pr-4 font-mono">{it.type}</td>
                                <td className="py-2 pr-4">{it.short_desc}</td>
                                <td className="py-2 pr-4">{it.sign}</td>
                                <td className="py-2 pr-4">{it.sub_type || '-'}</td>
                                <td className="py-2 pr-4">{it.def ? "Yes" : "No"}</td>
                                <td className="py-2 pr-4">
                                    <button
                                        onClick={() => toggleActive(it.type, it.is_active)}
                                        className={`px-3 py-1 rounded-md ${it.is_active ? "bg-green-600 text-white" : "bg-gray-300 text-gray-800"}`}
                                    >{it.is_active ? "Active" : "Inactive"}</button>
                                </td>
                                <td className="py-2 pr-4">
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => editItem(it)}
                                            className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                        >Edit</button>
                                        <button
                                            onClick={() => deleteItem(it.type, it.counter || 0)}
                                            className={`px-3 py-1 rounded-md ${(it.counter || 0) > 0
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-red-500 text-white hover:bg-red-600'
                                                }`}
                                            disabled={(it.counter || 0) > 0}
                                        >Delete</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Page>
    );
}
